import React from 'react'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { Box } from '@mui/material'
import Logo from '../../../../assets/img/dostyq-online.png'
import { StudentWeekReportState } from '../../../../features/types'
import { capitalize } from '../../../../utils/functions'
import { ui } from '../../../../utils/uiTokens'
import ReportStudentCard from './ReportStudentCard'

const PRACTICE_LABEL: Record<string, string> = {
    WITH_CAMERA: 'withCamera',
    WITHOUT_CAMERA: 'withoutCamera',
    ABSENT: 'absent',
}

interface Props {
    report: StudentWeekReportState
    monthNumber?: number
    weekNumber?: number
}

// Только для чтения: печатный лист без полей редактирования.
// Редактируемая версия живёт в StudentReportPage и не трогается.
const ReportSheet: React.FC<Props> = ({ report, monthNumber, weekNumber }) => {
    const { formatMessage, locale } = useIntl()
    const summary = report.summary
    const score = report.weekScore
    const videoPoint = summary.videoPoint + summary.conspectPoint

    const period = [
        monthNumber ? `${monthNumber} ${formatMessage({ id: 'month' })}` : '',
        weekNumber ? `${weekNumber} ${formatMessage({ id: 'week' })}` : '',
    ]
        .filter(Boolean)
        .join(' · ')

    const cards = [
        { id: 'video', value: summary.videoPoint, max: summary.videoTotal * 20, color: ui.pink },
        { id: 'test', value: summary.testPoint, max: summary.testTotal * 20, color: ui.pink },
        { id: 'conspect', value: summary.conspectPoint, max: summary.conspectMax, color: ui.pink },
        { id: 'zachet', value: summary.zachetPoint, max: 20, color: ui.pink },
        { id: 'practice', value: summary.practicePoint, max: 40, color: ui.pink },
    ]

    return (
        <Sheet>
            <SheetHeader>
                <Box>
                    <img src={Logo} alt="Dostyq Online" style={{ width: 150 }} />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <ReportTitle>{formatMessage({ id: 'weeklyReport' })}</ReportTitle>
                    <Pink>{period}</Pink>
                </Box>
            </SheetHeader>

            <ReportStudentCard
                firstName={summary.firstName}
                lastName={summary.lastName}
                subtitle={`${summary.groupTitle}${report[`subjectTitle${capitalize(locale)}` as keyof StudentWeekReportState] ? ` · ${report[`subjectTitle${capitalize(locale)}` as keyof StudentWeekReportState]}` : ''}`}
                totalPoint={summary.totalPoint}
                maxPoint={summary.maxPoint}
                rank={report.rank}
            />

            <Cards>
                {cards.map((card) => (
                    <MetricCard key={card.id} color={card.color}>
                        <Muted>{formatMessage({ id: card.id })}</Muted>
                        <MetricValue>
                            {card.value}
                            <small> / {card.max}</small>
                        </MetricValue>
                    </MetricCard>
                ))}
            </Cards>

            <SectionTitle color={ui.pink}>
                1. {formatMessage({ id: 'videoLessonsAndConspect' })}
                <Badge>{formatMessage({ id: 'automatic' })}</Badge>
            </SectionTitle>
            <ReportTable>
                <thead>
                    <tr>
                        <th>{formatMessage({ id: 'topic' })}</th>
                        <th>{formatMessage({ id: 'watched' })}</th>
                        <th>{formatMessage({ id: 'conspect' })}</th>
                        <th className="right">{formatMessage({ id: 'score' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {report.videoRows.map((row, index) => (
                        <tr key={index}>
                            <td>{row.videoTitle || row.topicTitle}</td>
                            <td>{row.watchedPercent}%</td>
                            <td>{row.conspectPoint ? `+${row.conspectPoint}` : '-'}</td>
                            <td className="right">{row.point}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3}>{formatMessage({ id: 'totalPoint' })}</td>
                        <td className="right">{videoPoint}</td>
                    </tr>
                </tfoot>
            </ReportTable>

            <SectionTitle color={ui.pink}>
                2. {formatMessage({ id: 'topicTestResults' })}
                <Badge>{formatMessage({ id: 'automatic' })}</Badge>
            </SectionTitle>
            <ReportTable>
                <thead>
                    <tr>
                        <th>{formatMessage({ id: 'test' })}</th>
                        <th>{formatMessage({ id: 'correctAnswer' })}</th>
                        <th>%</th>
                        <th className="right">{formatMessage({ id: 'score' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {report.testRows.map((row, index) => (
                        <tr key={index}>
                            <td>{row.testTitle}</td>
                            <td>
                                {row.point} / {row.maxPoint}
                            </td>
                            <td>{row.percent}%</td>
                            <td className="right">{row.ratingPoint}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3}>{formatMessage({ id: 'totalPoint' })}</td>
                        <td className="right">{summary.testPoint}</td>
                    </tr>
                </tfoot>
            </ReportTable>

            <SectionTitle color={ui.pink}>
                3. {formatMessage({ id: 'practiceAttendance' })}
                <Badge>{formatMessage({ id: 'systemAndMentor' })}</Badge>
            </SectionTitle>
            <PlainCard>
                <Box sx={{ fontSize: '15px', fontWeight: 600, color: ui.ink }}>
                    {score?.practiceStatus
                        ? formatMessage({ id: PRACTICE_LABEL[score.practiceStatus] })
                        : formatMessage({ id: 'notMarked' })}
                </Box>
                <Muted>{summary.practicePoint} / 40</Muted>
            </PlainCard>

            <SectionTitle color={ui.pink}>
                4. {formatMessage({ id: 'zachetGrade' })}
                <Badge>{formatMessage({ id: 'mentorSets' })}</Badge>
            </SectionTitle>
            <ZachetRow>
                <ZachetCard>
                    <Muted>{formatMessage({ id: 'zachet' })}</Muted>
                    <MetricValue>
                        {summary.zachetPoint}
                        <small> / 20</small>
                    </MetricValue>
                </ZachetCard>
                <PlainCard style={{ flexGrow: 1 }}>
                    <Muted>{formatMessage({ id: 'gradeScale' })}</Muted>
                    <ScaleTrack>
                        <ScaleFill style={{ width: `${(summary.zachetPoint / 20) * 100}%` }} />
                    </ScaleTrack>
                    <ScaleTicks>
                        <span>0</span>
                        <span>5</span>
                        <span>10</span>
                        <span>15</span>
                        <span>20</span>
                    </ScaleTicks>
                    <Comment>{score?.zachetComment || ''}</Comment>
                </PlainCard>
            </ZachetRow>

            <SectionTitle color={ui.pink}>5. {formatMessage({ id: 'mentorFeedback' })}</SectionTitle>
            <FeedbackBox>
                <Comment>{score?.mentorComment || ''}</Comment>
            </FeedbackBox>

            <SignatureRow>
                <SignatureBox>
                    <Muted>{formatMessage({ id: 'mentorSignature' })}</Muted>
                </SignatureBox>
                <SignatureBox>
                    <Muted>{formatMessage({ id: 'reportDate' })}</Muted>
                </SignatureBox>
            </SignatureRow>


            <Footnote>{formatMessage({ id: 'reportFootnote' })}</Footnote>
        </Sheet>
    )
}

export default ReportSheet

const Sheet = styled.div`
    background: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 32px;
    max-width: 900px;
    margin: 0 auto 24px;

    @media print {
        border: none;
        border-radius: 0;
        padding: 0;
        max-width: none;
        margin: 0;
    }
`

const SheetHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 3px solid ${ui.pink};
`

const ReportTitle = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: ${ui.ink};
`

const Muted = styled.div`
    font-size: 12px;
    color: ${ui.muted};
`

const Pink = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${ui.pink};
`






const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin: 20px 0 8px;
`

const MetricCard = styled.div<{ color: string }>`
    padding: 14px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
    border-top: 3px solid ${({ color }) => color};
`

const MetricValue = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${ui.ink};

    small {
        font-size: 12px;
        color: ${ui.muted};
    }
`

const SectionTitle = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 24px 0 12px;
    padding-left: 10px;
    border-left: 4px solid ${({ color }) => color};
    font-size: 15px;
    font-weight: 700;
    color: ${ui.ink};
`

const Badge = styled.span`
    margin-left: auto;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${ui.sub};
    background: ${ui.track};
`

const ReportTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        padding: 10px 12px;
        background: ${ui.surface};
        color: ${ui.muted};
        font-weight: 600;
        font-size: 12px;
    }

    td {
        padding: 10px 12px;
        border-top: 1px solid ${ui.border};
        color: ${ui.ink};
    }

    tfoot td {
        font-weight: 700;
        background: ${ui.surface};
    }

    .right {
        text-align: right;
    }
`

const PlainCard = styled.div`
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
`

const ZachetRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: stretch;
    flex-wrap: wrap;
`

const ZachetCard = styled.div`
    min-width: 160px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
    background: ${ui.surface};
    text-align: center;
`

const ScaleTrack = styled.div`
    height: 10px;
    margin: 10px 0 6px;
    border-radius: 100px;
    background: ${ui.track};
    overflow: hidden;
`

const ScaleFill = styled.div`
    height: 100%;
    border-radius: 100px;
    background: ${ui.pink};
`

const ScaleTicks = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: ${ui.muted};
`

const Comment = styled.div`
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.6;
    color: ${ui.ink};
    white-space: pre-wrap;
`

const FeedbackBox = styled.div`
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
    min-height: 80px;
`

const SignatureRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-top: 24px;
`

const SignatureBox = styled.div`
    padding-top: 28px;
    border-top: 1px solid ${ui.ink};
`





const Footnote = styled.div`
    margin-top: 12px;
    font-size: 11px;
    color: ${ui.muted};
`
